/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRemoteSessionsRequest,
  ListRemoteSessionsSecurity,
} from "../models/operations/listremotesessions.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRemoteSessionsInfiniteQuery,
  buildRemoteSessionsQuery,
  prefetchRemoteSessions,
  prefetchRemoteSessionsInfinite,
  queryKeyRemoteSessions,
  queryKeyRemoteSessionsInfinite,
  RemoteSessionsInfiniteQueryData,
  RemoteSessionsPageParams,
  RemoteSessionsQueryData,
} from "./remoteSessions.core.js";
export {
  buildRemoteSessionsInfiniteQuery,
  buildRemoteSessionsQuery,
  prefetchRemoteSessions,
  prefetchRemoteSessionsInfinite,
  queryKeyRemoteSessions,
  queryKeyRemoteSessionsInfinite,
  type RemoteSessionsInfiniteQueryData,
  type RemoteSessionsPageParams,
  type RemoteSessionsQueryData,
};

export type RemoteSessionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRemoteSessions remoteSessions
 *
 * @remarks
 * List remote_sessions in the caller's project. Supplying both principal_id and user_session_issuer_id instead lists only the ordinary human caller's eligible sessions for an agent they own, without requiring project read permission. Both filters must be supplied together. access_token_encrypted and refresh_token_encrypted are never returned — only metadata (access_expires_at, refresh_expires_at, authorization_expires_at, scopes).
 */
export function useRemoteSessions(
  request?: ListRemoteSessionsRequest | undefined,
  security?: ListRemoteSessionsSecurity | undefined,
  options?: QueryHookOptions<RemoteSessionsQueryData, RemoteSessionsQueryError>,
): UseQueryResult<RemoteSessionsQueryData, RemoteSessionsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRemoteSessionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRemoteSessions remoteSessions
 *
 * @remarks
 * List remote_sessions in the caller's project. Supplying both principal_id and user_session_issuer_id instead lists only the ordinary human caller's eligible sessions for an agent they own, without requiring project read permission. Both filters must be supplied together. access_token_encrypted and refresh_token_encrypted are never returned — only metadata (access_expires_at, refresh_expires_at, authorization_expires_at, scopes).
 */
export function useRemoteSessionsSuspense(
  request?: ListRemoteSessionsRequest | undefined,
  security?: ListRemoteSessionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RemoteSessionsQueryData,
    RemoteSessionsQueryError
  >,
): UseSuspenseQueryResult<RemoteSessionsQueryData, RemoteSessionsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRemoteSessionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRemoteSessions remoteSessions
 *
 * @remarks
 * List remote_sessions in the caller's project. Supplying both principal_id and user_session_issuer_id instead lists only the ordinary human caller's eligible sessions for an agent they own, without requiring project read permission. Both filters must be supplied together. access_token_encrypted and refresh_token_encrypted are never returned — only metadata (access_expires_at, refresh_expires_at, authorization_expires_at, scopes).
 */
export function useRemoteSessionsInfinite(
  request?: ListRemoteSessionsRequest | undefined,
  security?: ListRemoteSessionsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    RemoteSessionsInfiniteQueryData,
    RemoteSessionsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<RemoteSessionsInfiniteQueryData, RemoteSessionsPageParams>,
  RemoteSessionsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    RemoteSessionsInfiniteQueryData,
    RemoteSessionsQueryError,
    InfiniteData<RemoteSessionsInfiniteQueryData, RemoteSessionsPageParams>,
    QueryKey,
    RemoteSessionsPageParams
  >({
    ...buildRemoteSessionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listRemoteSessions remoteSessions
 *
 * @remarks
 * List remote_sessions in the caller's project. Supplying both principal_id and user_session_issuer_id instead lists only the ordinary human caller's eligible sessions for an agent they own, without requiring project read permission. Both filters must be supplied together. access_token_encrypted and refresh_token_encrypted are never returned — only metadata (access_expires_at, refresh_expires_at, authorization_expires_at, scopes).
 */
export function useRemoteSessionsInfiniteSuspense(
  request?: ListRemoteSessionsRequest | undefined,
  security?: ListRemoteSessionsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    RemoteSessionsInfiniteQueryData,
    RemoteSessionsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<RemoteSessionsInfiniteQueryData, RemoteSessionsPageParams>,
  RemoteSessionsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    RemoteSessionsInfiniteQueryData,
    RemoteSessionsQueryError,
    InfiniteData<RemoteSessionsInfiniteQueryData, RemoteSessionsPageParams>,
    QueryKey,
    RemoteSessionsPageParams
  >({
    ...buildRemoteSessionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setRemoteSessionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      principalId?: string | undefined;
      userSessionIssuerId?: string | undefined;
      subjectUrn?: string | undefined;
      remoteSessionClientId?: string | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RemoteSessionsQueryData,
): RemoteSessionsQueryData | undefined {
  const key = queryKeyRemoteSessions(...queryKeyBase);

  return client.setQueryData<RemoteSessionsQueryData>(key, data);
}

export function invalidateRemoteSessions(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      principalId?: string | undefined;
      userSessionIssuerId?: string | undefined;
      subjectUrn?: string | undefined;
      remoteSessionClientId?: string | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessions", "list", ...queryKeyBase],
  });
}

export function invalidateAllRemoteSessions(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessions", "list"],
  });
}
